/* ──────────────────────────────────────────────────────────────
   src/pages/reports/clientes/ProfesoresAlumnoPage.tsx
   Informe “Profesores por alumno”
   ────────────────────────────────────────────────────────────── */
import { useState } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  TextField,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  LinearProgress,
  Alert,
  InputAdornment,
  TableSortLabel,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useProfesoresAlumnoReport } from '../../../hooks/useReportJob';
import { useClientes } from '../../../hooks/useClientes';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

export default function ProfesoresAlumnoPage() {
  const { t } = useTranslation<'translation'>();
  /* ─── filtros básicos ─── */
  const today = dayjs().format('YYYY-MM-DD');
  const [desde, setDesde] = useState(today);
  const [hasta, setHasta] = useState(today); // ← nuevo filtro
  const [alumnoSel, setAlumnoSel] = useState<number>(); // id alumno activo
  const [jobId, setJobId] = useState<string>();
  const [searchCli, setSearchCli] = useState('');
  const [searchProf, setSearchProf] = useState('');
  const [sortAsc, setAsc] = useState(true);

  /* ─── catálogos e informe ─── */
  const { rows: clientes } = useClientes(); // sólo rol=cliente
  const { run, useStatus } = useProfesoresAlumnoReport();
  const { data: status } = useStatus(jobId);

  /* ────────── lanzar informe ────────── */
  const handleSelect = async (id: number) => {
    setAlumnoSel(id);
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error('Error al descargar:', error);
      }
    }
  };

  /* lista lateral filtrada */
  const clientesFiltrados = clientes.filter((c) =>
    c.nombreCompleto.toLowerCase().includes(searchCli.toLowerCase()),
  );

  /* resultado filtrado + ordenado */
  const filas =
    status?.result?.rows
      .filter((r) => r.alumnoId === alumnoSel)
      .filter((r) => r.profesor.toLowerCase().includes(searchProf.toLowerCase()))
      .sort((a, b) => {
        if (sortAsc) return a.ultimaReserva > b.ultimaReserva ? 1 : -1;
        return a.ultimaReserva < b.ultimaReserva ? 1 : -1;
      }) ?? [];

  /* ─────────────────── UI ─────────────────── */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('profesoresAlumno.titulo')}</Typography>

      {/* ——— barra superior ——— */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          label={t('profesoresAlumno.filtros.desde')}
          type="date"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />

        <TextField
          label={t('profesoresAlumno.filtros.hasta') || 'Hasta'} // clave i18n opcional
          type="date"
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />

        <TextField
          size="small"
          placeholder={t<string>('profesoresAlumno.filtros.buscarAlumno')}
          value={searchCli}
          onChange={(e) => setSearchCli(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ ml: 2, width: 260 }}
        />
      </Stack>

      {/* ——— lista de alumnos ——— */}
      <Paper sx={{ maxHeight: 300, overflowY: 'auto', p: 1 }}>
        <Table size="small">
          <TableBody>
            {clientesFiltrados.map((c) => (
              <TableRow
                key={c.id}
                hover
                selected={c.id === alumnoSel}
                onClick={() => handleSelect(c.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{c.nombreCompleto}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* progreso worker */}
      {status && !status.done && !status.failed && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t('profesoresAlumno.estado.generando', {
              progress: status.progress,
            })}
          </Typography>
        </Stack>
      )}

      {/* ——— resultado ——— */}
      {status?.done && (
        <>
          <Alert severity="success">
            {t('profesoresAlumno.estado.exito')}{' '}
            <Button
              variant="text"
              size="small"
              onClick={handleDownload}
              sx={{ textTransform: 'none', textDecoration: 'underline', p: 0, minWidth: 'auto' }}
            >
              {t('profesoresAlumno.estado.descargar')}
            </Button>{' '}
            {t('profesoresAlumno.estado.filas', {
              count: status.result!.registros,
            })}
          </Alert>

          <TextField
            size="small"
            placeholder={t<string>('profesoresAlumno.filtros.buscarProfesor')}
            value={searchProf}
            onChange={(e) => setSearchProf(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 260 }}
          />

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>{t('profesoresAlumno.tabla.profesor')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('profesoresAlumno.tabla.asignaturas')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t('profesoresAlumno.tabla.clases')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t('profesoresAlumno.tabla.horas')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active
                      direction={sortAsc ? 'asc' : 'desc'}
                      onClick={() => setAsc(!sortAsc)}
                    >
                      {t('profesoresAlumno.tabla.ultimaClase')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filas.map((r) => (
                  <TableRow key={`${r.profesorId}-${r.alumnoId}`}>
                    <TableCell>{r.profesor}</TableCell>
                    <TableCell>{r.asignaturas || '—'}</TableCell>
                    <TableCell align="right">{r.numClases}</TableCell>
                    <TableCell align="right">{r.horas}</TableCell>
                    <TableCell align="right">{r.ultimaReserva}</TableCell>
                  </TableRow>
                ))}

                {filas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t('profesoresAlumno.tabla.vacio')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Stack>
  );
}
